import type {
  AuditEventResponse,
  CostReport,
  Entity,
  HealthReport,
  QualityReport,
  RecommendationReport,
  UsageReport,
} from "@esi/shared";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { apiError, ok } from "../../api-errors.ts";
import type { ReportTab } from "../../router.tsx";
import { useAdminApi } from "../admin-api.tsx";

export interface ReportFilters {
  from?: string | undefined;
  to?: string | undefined;
  entity?: string | undefined;
}

export interface Reports {
  usage: UsageReport;
  quality: QualityReport;
  cost: CostReport;
  health: HealthReport;
}

const query = (f: ReportFilters) => ({
  ...(f.from && { from: f.from }),
  ...(f.to && { to: f.to }),
  ...(f.entity && { entity: f.entity }),
});

type Admin = ReturnType<typeof useAdminApi>["admin"];

const fetchers: {
  [K in ReportTab]: (admin: Admin, f: ReportFilters) => Promise<Reports[K]>;
} = {
  usage: async (admin, f) => ok(await admin.reports.usage.$get({ query: query(f) })),
  quality: async (admin, f) => ok(await admin.reports.quality.$get({ query: query(f) })),
  cost: async (admin, f) => ok(await admin.reports.cost.$get({ query: query(f) })),
  health: async (admin, f) => ok(await admin.reports.health.$get({ query: query(f) })),
};

/**
 * One report for the filters. The previous figures of the same report stay on screen while new
 * ones load (no flash between periods), never another report's (their shapes differ); figures
 * change hourly, so a minute of staleness is fine.
 */
export function useReport<K extends ReportTab>(tab: K, filters: ReportFilters) {
  const { admin } = useAdminApi();
  return useQuery({
    queryKey: ["admin", "reports", tab, filters.from ?? "", filters.to ?? "", filters.entity ?? ""],
    queryFn: () => fetchers[tab](admin, filters) as Promise<Reports[K]>,
    placeholderData: (previous, query) =>
      query?.queryKey[2] === tab ? keepPreviousData(previous) : undefined,
    retry: false,
    staleTime: 60 * 1000,
  });
}

/**
 * Recommendation acceptance for the filters (recommendation-outcomes), beside the quality
 * report: counts only, the caller's scope.
 */
export function useRecommendations(filters: ReportFilters) {
  const { admin } = useAdminApi();
  return useQuery({
    queryKey: [
      "admin",
      "reports",
      "recommendations",
      filters.from ?? "",
      filters.to ?? "",
      filters.entity ?? "",
    ],
    queryFn: async (): Promise<RecommendationReport> =>
      // Without `format`, the route answers the report (the export is the other branch).
      (await ok(
        await admin.reports.recommendations.$get({ query: query(filters) }),
      )) as RecommendationReport,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 60 * 1000,
  });
}

/** The tuning export of the same figures as a file (tenant admins). */
export function useRecommendationExport(filters: ReportFilters) {
  const { admin } = useAdminApi();
  return useMutation({
    mutationFn: async () => {
      const res = await admin.reports.recommendations.$get({
        query: { ...query(filters), format: "export" },
      });
      if (!res.ok) {
        throw await apiError(res);
      }
      const blob = await res.blob();
      const name =
        /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ??
        "recommendation-outcomes.json";
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      // Attached, and the URL kept a moment: some browsers cancel a detached or revoked one.
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });
}

/** The entities in the caller's scope (the API returns only those). */
export function useScopeEntities() {
  const { admin } = useAdminApi();
  return useQuery({
    queryKey: ["admin", "entities"],
    queryFn: async (): Promise<Entity[]> => ok(await admin.entities.$get()),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Whether the caller may read the audit log (tenant admins only; the server decides, `403`
 * otherwise). Asked with the smallest query, so links to audit events show only when they work.
 */
export function useAuditAccess(): boolean {
  const { admin } = useAdminApi();
  const probe = useQuery({
    queryKey: ["admin", "audit", "access"],
    queryFn: async (): Promise<AuditEventResponse[]> =>
      ok(await admin.audit.$get({ query: { limit: "1" } })),
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  return probe.isSuccess;
}
